/**
 * Number / unit formatting helpers used by chart wrappers and KPI tiles.
 * Kept centralized so a percentage in the dashboard sparkline reads the
 * same way as one in the resource-detail line chart.
 */

import type { ChartUnit } from './types';

const compactNumber = new Intl.NumberFormat(undefined, {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const decimalNumber = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 2,
});

const integerNumber = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 0,
});

/**
 * Up to 2 decimals, but never pads with zeros — `0.4` reads as "0.4",
 * `38` as "38", `99.92` as "99.92". Used for both KPI tile values and
 * y-axis ticks so the two never disagree (the previous integer-only
 * tick formatter collapsed `0.4%` → "0%" on small axes and `99.92%` →
 * "100%" on uptime axes).
 */
const percentFormat = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 2,
  minimumFractionDigits: 0,
});

const usdFormat = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

const usdTickFormat = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

const KIB = 1024;
const KIB_THRESHOLD = 100;
const MIB = 1024 * 1024;
const MIB_THRESHOLD = 100;
const GIB = 1024 * 1024 * 1024;
const TIB = 1024 * 1024 * 1024 * 1024;

const MS_THRESHOLD = 1000;
const SEC_THRESHOLD = 60;
const PERCENT_FRACTIONAL = 100;

/** Format a numeric value according to a unit hint. Compact for big counts. */
export function formatValue(value: number, unit: ChartUnit | undefined): string {
  if (!Number.isFinite(value)) {
    return '—';
  }
  switch (unit) {
    case 'percent':
      return `${percentFormat.format(value)}%`;
    case 'ms': {
      const abs = Math.abs(value);
      if (abs < MS_THRESHOLD) {
        return `${integerNumber.format(value)} ms`;
      }
      const seconds = value / MS_THRESHOLD;
      if (Math.abs(seconds) < SEC_THRESHOLD) {
        return `${decimalNumber.format(seconds)} s`;
      }
      return `${decimalNumber.format(seconds / SEC_THRESHOLD)} m`;
    }
    case 'bytes':
      return formatBytes(value);
    case 'rate':
      return `${decimalNumber.format(value)}/s`;
    case 'usd':
      return usdFormat.format(value);
    case 'count':
    case undefined:
    default:
      return Math.abs(value) >= KIB_THRESHOLD
        ? compactNumber.format(value)
        : decimalNumber.format(value);
  }
}

/** Y-axis tick formatter. Compact for `count`, bare percent number for `percent`. */
export function tickFormatter(unit: ChartUnit | undefined) {
  return (value: number): string => {
    if (!Number.isFinite(value)) {
      return '';
    }
    if (unit === 'percent') {
      return `${percentFormat.format(value)}%`;
    }
    if (unit === 'ms') {
      return integerNumber.format(value);
    }
    if (unit === 'bytes') {
      return formatBytes(value);
    }
    if (unit === 'usd') {
      return usdTickFormat.format(value);
    }
    return Math.abs(value) >= KIB_THRESHOLD
      ? compactNumber.format(value)
      : integerNumber.format(value);
  };
}

function formatBytes(bytes: number): string {
  const abs = Math.abs(bytes);
  if (abs < KIB) {
    return `${integerNumber.format(bytes)} B`;
  }
  if (abs < KIB * KIB_THRESHOLD) {
    return `${decimalNumber.format(bytes / KIB)} KiB`;
  }
  if (abs < MIB * MIB_THRESHOLD) {
    return `${decimalNumber.format(bytes / MIB)} MiB`;
  }
  if (abs < TIB) {
    return `${decimalNumber.format(bytes / GIB)} GiB`;
  }
  return `${decimalNumber.format(bytes / TIB)} TiB`;
}

/** Convert a fractional uptime like 0.999 → "99.90%". */
export function uptimePctToString(fraction: number): string {
  if (!Number.isFinite(fraction)) {
    return '—';
  }
  return `${(fraction * PERCENT_FRACTIONAL).toFixed(2)}%`;
}
